import {
  GameSession,
  GameMode,
  AIDifficulty,
  Player,
  PlayerType,
  GameStatus,
  GameSettings,
  Board,
  Position,
  MoveResult,
  GameState,
  WinResult,
  Mark,
  Cell
} from '../types';
import { 
  createEmptyBoard, 
  generateId, 
  isValidGameSession, 
  isValidPosition,
  isValidMove
} from '../utils/validation';

export class GameEngine {
  private games: Map<string, GameSession> = new Map();
  
  /**
   * Gets all active games (for debugging/testing purposes)
   */
  getActiveGames(): GameSession[] {
    return Array.from(this.games.values());
  }
  
  /**
   * Removes a game from memory
   */
  removeGame(gameId: string): boolean {
    return this.games.delete(gameId);
  }
  
  /**
   * Updates a game session
   */
  updateGame(gameSession: GameSession): boolean {
    if (!isValidGameSession(gameSession)) {
      return false;
    }
    
    this.games.set(gameSession.id, gameSession);
    return true;
  }

  /**
   * Creates a new game session
   */
  createGame(mode: GameMode, difficulty?: AIDifficulty): GameSession {
    // Validate mode
    if (!(mode === GameMode.SINGLE_PLAYER || mode === GameMode.MULTIPLAYER)) {
      throw new Error('Invalid game mode');
    }
    
    // Validate difficulty for single-player mode
    if (mode === GameMode.SINGLE_PLAYER) {
      if (!difficulty) {
        throw new Error('Difficulty is required for single-player mode');
      }
      if (!(difficulty === AIDifficulty.EASY || difficulty === AIDifficulty.MEDIUM || difficulty === AIDifficulty.HARD)) {
        throw new Error('Invalid AI difficulty level');
      }
    }
    
    const gameId = generateId();
    const board = createEmptyBoard();
    
    // Create human player
    const humanPlayer: Player = {
      id: generateId(),
      name: 'Player 1',
      type: PlayerType.HUMAN
    };

    const players: Player[] = [humanPlayer];
    
    // Add AI player for single-player mode
    if (mode === GameMode.SINGLE_PLAYER) {
      const aiPlayer: Player = {
        id: generateId(),
        name: `AI (${difficulty})`,
        type: PlayerType.AI,
        difficulty: difficulty
      };
      
      players.push(aiPlayer);
    }

    const settings: GameSettings = {
      mode,
      theme: 'default',
      soundEnabled: true,
      realTimeMode: true
    };

    const gameSession: GameSession = {
      id: gameId,
      mode,
      players,
      board,
      currentPlayer: humanPlayer.id,
      status: GameStatus.IN_PROGRESS,
      createdAt: new Date(),
      settings
    };

    // Validate the created game session
    if (!isValidGameSession(gameSession)) {
      throw new Error('Created game session is invalid');
    }

    this.games.set(gameId, gameSession);
    return gameSession;
  }

  /**
   * Makes a move in the game
   */
  makeMove(gameId: string, position: Position, playerId: string): MoveResult {
    // Validate inputs
    if (!gameId || typeof gameId !== 'string') {
      return { success: false, error: 'Invalid game ID' };
    }
    
    if (!playerId || typeof playerId !== 'string') {
      return { success: false, error: 'Invalid player ID' };
    }
    
    if (!isValidPosition(position)) {
      return { success: false, error: 'Invalid position' };
    }

    const game = this.games.get(gameId);
    if (!game) {
      return { success: false, error: 'Game not found' };
    }

    if (game.status !== GameStatus.IN_PROGRESS) {
      return { success: false, error: 'Game is not in progress' };
    }

    if (game.currentPlayer !== playerId) {
      return { success: false, error: 'Not your turn' };
    }

    if (!this.validateMove(gameId, position)) {
      return { success: false, error: 'Cell is already occupied' };
    }

    // Find player and determine mark
    const player = game.players.find(p => p.id === playerId);
    if (!player) {
      return { success: false, error: 'Player not found in this game' };
    }

    // Determine mark based on player order (first player gets X, second gets O)
    const playerIndex = game.players.findIndex(p => p.id === playerId);
    const mark = playerIndex === 0 ? Mark.X : Mark.O;

    // Make the move
    game.board.cells[position.row][position.col] = {
      position,
      mark,
      playerId
    };

    // Check for win condition
    const winResult = this.checkWinCondition(game.board);
    
    if (winResult.hasWinner || winResult.isDraw) {
      game.status = GameStatus.COMPLETED;
    } else {
      // Switch to next player
      const currentPlayerIndex = game.players.findIndex(p => p.id === playerId);
      const nextPlayerIndex = (currentPlayerIndex + 1) % game.players.length;
      game.currentPlayer = game.players[nextPlayerIndex].id;
    }

    const gameState: GameState = {
      board: game.board,
      currentPlayer: game.currentPlayer,
      status: game.status,
      winner: winResult.winner,
      winningCells: winResult.winningCells
    };

    return {
      success: true,
      gameState,
      winResult
    };
  }

  /**
   * Gets the current game state
   */
  getGameState(gameId: string): GameState | null {
    const game = this.games.get(gameId);
    if (!game) {
      return null;
    }

    return {
      board: game.board,
      currentPlayer: game.currentPlayer,
      status: game.status
    };
  }

  /**
   * Validates if a move is legal
   */
  validateMove(gameId: string, position: Position): boolean {
    const game = this.games.get(gameId);
    if (!game) {
      return false;
    }

    if (!isValidPosition(position)) {
      return false;
    }

    // Check if cell is empty
    const cell = game.board.cells[position.row][position.col];
    return cell.mark === null;
  }

  /**
   * Checks for win conditions
   */
  checkWinCondition(board: Board): WinResult {
    const cells = board.cells;
    
    // Check rows
    for (let row = 0; row < 3; row++) {
      if (cells[row][0].mark && 
          cells[row][0].mark === cells[row][1].mark && 
          cells[row][1].mark === cells[row][2].mark) {
        return {
          hasWinner: true,
          winner: cells[row][0].playerId!,
          winningCells: [
            { row, col: 0 },
            { row, col: 1 },
            { row, col: 2 }
          ],
          isDraw: false
        };
      }
    }

    // Check columns
    for (let col = 0; col < 3; col++) {
      if (cells[0][col].mark && 
          cells[0][col].mark === cells[1][col].mark && 
          cells[1][col].mark === cells[2][col].mark) {
        return {
          hasWinner: true,
          winner: cells[0][col].playerId!,
          winningCells: [
            { row: 0, col },
            { row: 1, col },
            { row: 2, col }
          ],
          isDraw: false
        };
      }
    }

    // Check diagonals
    if (cells[0][0].mark && 
        cells[0][0].mark === cells[1][1].mark && 
        cells[1][1].mark === cells[2][2].mark) {
      return {
        hasWinner: true,
        winner: cells[0][0].playerId!,
        winningCells: [
          { row: 0, col: 0 },
          { row: 1, col: 1 },
          { row: 2, col: 2 }
        ],
        isDraw: false
      };
    }

    if (cells[0][2].mark && 
        cells[0][2].mark === cells[1][1].mark && 
        cells[1][1].mark === cells[2][0].mark) {
      return {
        hasWinner: true,
        winner: cells[0][2].playerId!,
        winningCells: [
          { row: 0, col: 2 },
          { row: 1, col: 1 },
          { row: 2, col: 0 }
        ],
        isDraw: false
      };
    }

    // Check for draw (board full)
    let isFull = true;
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        if (cells[row][col].mark === null) {
          isFull = false;
          break;
        }
      }
      if (!isFull) break;
    }

    return {
      hasWinner: false,
      isDraw: isFull
    };
  }

  /**
   * Gets a game session by ID
   */
  getGame(gameId: string): GameSession | undefined {
    return this.games.get(gameId);
  }
}